import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import { r6, t } from '../copy'
import { useDictation } from '../lib/useDictation'
import { Arrow, Mic, Search } from '../ui/icons'

const MAX = 300

/**
 * "Ask": one line, typed or dictated, sent on Enter or the arrow (nothing is sent while typing).
 * `examples` shows a few tappable asks under the empty bar; tapping one sends it.
 */
export function AskBar({ onAsk, examples = false, busy = false, initial = '', autoFocus = false, placeholder, className = '' }: {
  onAsk: (q: string) => void
  /** A shorter prompt where the bar is narrow (the menu on a phone). */
  placeholder?: string
  examples?: boolean
  busy?: boolean
  initial?: string
  autoFocus?: boolean
  className?: string
}) {
  const reduce = useReducedMotion() ?? false
  const [q, setQ] = useState(initial)
  const dictation = useDictation()
  const latest = useRef(q)
  useEffect(() => {
    latest.current = q
  })
  const mic = dictation.supported && !dictation.denied
  const ready = q.trim().length >= 3 && !busy

  const send = (text = q) => {
    const v = text.trim()
    if (v.length < 3 || busy) return
    if (dictation.listening) dictation.stop()
    onAsk(v)
  }

  return (
    <div className={className}>
      <form
        role="search"
        onSubmit={(e) => {
          e.preventDefault()
          send()
        }}
        className="flex min-h-12 items-center gap-1 rounded-full bg-surface pl-4 pr-1 ring-1 ring-inset ring-line focus-within:ring-2 focus-within:ring-ink"
      >
        <Search size={19} className="text-ink-2" />
        <input
          type="search"
          enterKeyHint="search"
          autoFocus={autoFocus}
          value={q}
          maxLength={MAX}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder ?? r6('ask.placeholder')}
          aria-label={r6('ask.label')}
          className="min-w-0 flex-1 bg-transparent py-3 pl-1.5 text-[17px] leading-6 text-ink outline-none placeholder:text-ink-3 [&::-webkit-search-cancel-button]:hidden"
        />
        {mic && (
          <button
            type="button"
            onClick={() => (dictation.listening ? dictation.stop() : dictation.start(latest.current, (text) => setQ(text.slice(0, MAX))))}
            aria-pressed={dictation.listening}
            aria-label={dictation.listening ? t('saturday.mic.stop') : t('saturday.mic.start')}
            title={dictation.listening ? t('saturday.mic.stop') : t('saturday.mic.start')}
            className={`press relative flex size-11 shrink-0 items-center justify-center rounded-full ${dictation.listening ? 'bg-accent text-on-accent' : 'text-ink'}`}
          >
            {dictation.listening && !reduce && (
              <motion.span aria-hidden="true" className="absolute inset-0 rounded-full bg-accent"
                initial={{ opacity: 0.45, scale: 1 }} animate={{ opacity: 0, scale: 1.6 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeOut' }} />
            )}
            <Mic size={19} className="relative" />
          </button>
        )}
        <button
          type="submit"
          aria-label={r6('ask.submit')}
          title={r6('ask.submit')}
          aria-disabled={!ready}
          className={`press flex size-10 shrink-0 items-center justify-center rounded-full transition-colors ${ready ? 'bg-accent text-on-accent' : 'bg-sunk text-ink-3'}`}
        >
          <Arrow dir="up" size={19} strokeWidth={2.4} />
        </button>
      </form>
      {examples && !q && (
        <div className="rail -mx-5 mt-2 items-center gap-2 px-5 md:-mx-8 md:px-8 lg:mx-0 lg:flex-wrap lg:px-0 lg:[mask-image:none]">
          <span className="t-meta shrink-0 text-ink-2">{r6('ask.try')}</span>
          {(['ask.example.1', 'ask.example.2', 'ask.example.3'] as const).map((k) => (
            <button key={k} type="button" onClick={() => { setQ(r6(k)); send(r6(k)) }}
              className="press inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-full bg-sunk px-3.5 text-[15px] font-medium text-ink">
              {r6(k)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
